/**
 * @fileOverview The player's timings, all at 1× — the speed control divides them (see `scaled` in
 * state.ts). See docs/view-help.md.
 */

/** How long a step's region may take to appear when the frame is already on its view. */
export const ANCHOR_TIMEOUT_MS = 1500

/** …after the pointer has clicked a tab, while the view it opened is still mounting. */
export const IN_FRAME_NAV_TIMEOUT_MS = 3000

/** …after the frame has been reloaded, which boots a whole app instance. */
export const FRAME_LOAD_TIMEOUT_MS = 8000

/** The camera's move from one region to the next. `LewViewHelpStage` animates it over this long. */
export const ZOOM_MS = 700

/** The camera's move for a seek: long enough to read as a move, short enough to feel immediate. */
export const SEEK_CAMERA_MS = 260

/**
 * How long each click of a way to another view may wait for its target: every one of them loads a
 * page — a lazy route, then that page's own data — before the next thing to click is there.
 */
export const WAY_TARGET_TIMEOUT_MS = 6000

/** Gliding a region into view inside the frame, before the camera moves onto it. */
export const SCROLL_MS = 420

/** The camera pulling back to the whole page before the page scrolls. Shorter than a full move. */
export const PULL_BACK_MS = 480

/** How long the pointer may wait for something it is about to use to appear — a dialog opening. */
export const ACTION_TIMEOUT_MS = 2500

/**
 * The camera while the pointer is working: a quicker move than between steps, and a gentler zoom.
 * The action's target is often a single field, and the full step zoom on one of those loses the form
 * it belongs to.
 */
export const ACTION_CAMERA_MS = 520
export const ACTION_ZOOM_CAP = 1.6

/** Typing: a beat per character, with a small uneven wobble so it reads as a hand. */
export const TYPE_MS = 70

/**
 * How often a step that is up re-measures its region, and how long the camera takes to follow when it
 * has moved. See `trackRegion`.
 */
export const TRACK_MS = 120
export const FOLLOW_MS = 200

/** After an action, time for the page to react — a dialog to open, a list to filter. */
export const AFTER_ACTION_MS = 320

/**
 * The same at speed, for an action replayed by a seek — still long enough for a Vuetify dialog's leave
 * transition (~225ms): a scene that is restarted reopens the same dialog, and reopening it
 * mid-transition lets its `after-leave` clear the form that has just been opened.
 */
export const AFTER_ACTION_AT_SPEED_MS = 260

/**
 * After a press on a map, played or at speed: the map pans onto the pin it has just placed (Leaflet's
 * pan animation, 250ms), and a press measured against a map still gliding lands somewhere else.
 */
export const MAP_PAN_MS = 320

/**
 * After typing, at least this long in real time — never shortened by the speed control. A picker's
 * server search waits 250ms after the last keystroke (`usePagedSearch`), and a pick made before that
 * search answered let its result arrive *after* the pick, into a field the reader had seen settle.
 */
export const SEARCH_SETTLE_MS = 340

/** Beats around a click: resting on the target, then holding the press, so both are visible. */
export const HOVER_MS = 220
export const PRESS_HOLD_MS = 140

/**
 * The extra rest on a link before it is pressed: a line of text that navigates shows its hover look
 * (`services/viewHelp/hover.ts`), and the page is gone the moment it is pressed — without the beat
 * the underline was on screen for less time than the eye takes to find it.
 */
export const LINK_HOVER_MS = 380

/**
 * The extra rest before a press made with a key held (Strg, Umschalt). The keycap beside the pointer
 * is the whole difference between that press and a plain one, and at the plain rest it was gone
 * before it had been read.
 */
export const HELD_KEY_MS = 520

/**
 * A right-click is held much longer, and stays in view after the menu opened: the reader cannot hear
 * which button went down, so the pulsing ring and the mouse badge beside the pointer have to be seen
 * — a 140ms press was over before the eye found it.
 */
export const RIGHT_HOLD_MS = 650
export const RIGHT_SHOW_MS = 700

/**
 * The beat a drag rests at its destination before it lets go, in real time — never shortened by the
 * speed control, and spent at speed too.
 *
 * It reads as a hand arriving and settling, and the page needs it: the gantt fills a selection from a
 * *debounced* `mouseover` (100ms after the pointer stops), and a release that came in before that had
 * nothing selected to pick up. A seek that replays a drag has to arrive at the same state as the
 * demonstration, so the beat cannot be animation-only.
 */
export const DRAG_HOLD_MS = 180

/** How long a dragged thing is shown lifted under the pointer before it starts to travel. */
export const LIFT_MS = 260

/** Between the Escapes a scene's backstop presses while an overlay is still open. */
export const ESCAPE_RETRY_MS = 180

/** The steps the speed control moves through. 1 is where it starts. */
export const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2] as const

/**
 * How long a step stays up before the player moves on: a reading time for its text.
 *
 * Roughly 24 characters a second on top of a fixed beat for finding the dot, clamped so that a
 * three-word step does not flash past and a long one does not stall the whole walkthrough.
 */
export const dwellFor = (text: string) => Math.min(9000, Math.max(3200, 1400 + text.length * 42))

/** What each example page a step shows adds to its reading time: the page is read too. */
export const IMAGE_READING_MS = 3000

export const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

/**
 * Read fresh each time rather than cached: somebody who turns the preference on mid-walkthrough
 * means it for the rest of it.
 */
export const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
